import { describeError, useApi } from '@classgo/app-kit';
import type { ConsentPurpose, ConsentTextView } from '@classgo/shared';
import { Badge, Button, Card, Input, Modal, Spinner, useToast } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { useT } from '../../i18n';
import { peopleApi, peopleQueries } from '../../people/api';
import { useTenant } from '../../tenant/tenant-provider';

const ORDER: readonly ConsentPurpose[] = ['notice', 'general', 'sensitive', 'ai'];
const SCHOOL_NAME = '{{schoolName}}';

/**
 * The texts families accept (ADR-0011): the privacy notice and the three authorizations. ClassGO
 * ships reviewed defaults; the school reads them with its own name and may publish its wording,
 * which becomes a new version while the authorizations already signed keep the text they
 * accepted. Opening the screen materializes the defaults, which completes the setup step.
 */
export const ConsentTextsScreen = () => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queries = useQueryClient();
  const { session } = useTenant();
  const canEdit = session.permissions.all || session.permissions.keys.includes('tenant:write');
  const texts = useQuery(peopleQueries.consentTexts(api));
  const [editing, setEditing] = useState<ConsentTextView | null>(null);
  const [draft, setDraft] = useState({ title: '', body: '' });
  const withSchool = (body: string) => body.split(SCHOOL_NAME).join(session.tenant.name);

  const publish = useMutation({
    mutationFn: (text: ConsentTextView) =>
      peopleApi(api).publishConsentText(text.purpose, {
        title: draft.title.trim(),
        body: draft.body.trim(),
      }),
    onSuccess: (published) => {
      toast.show({
        tone: 'success',
        title: t('consents.published', { version: published.version }),
      });
      setEditing(null);
      void queries.invalidateQueries({ queryKey: ['consents'] });
      void queries.invalidateQueries({ queryKey: ['setup'] });
    },
    onError: (error) =>
      toast.show({
        tone: 'error',
        title: t('consents.edit'),
        description: describeError(t, error),
      }),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (editing) publish.mutate(editing);
  };
  const ordered = ORDER.flatMap((purpose) => {
    const text = texts.data?.find((candidate) => candidate.purpose === purpose);
    return text ? [text] : [];
  });

  return (
    <>
      <Card title={t('consents.title')}>
        <p className="text-sm text-gray-600">{t('consents.intro')}</p>
        {texts.isPending ? (
          <div role="status" className="mt-4 flex items-center gap-2 text-sm text-gray-500">
            <Spinner /> {t('common.loading')}
          </div>
        ) : null}
        {texts.isError ? (
          <p role="alert" className="mt-4 text-sm text-danger-700">
            {describeError(t, texts.error)}
          </p>
        ) : null}
        <div className="mt-4 space-y-4">
          {ordered.map((text) => (
            <section key={text.purpose} className="rounded-card border border-gray-200 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900">{text.title}</h3>
                  {t(`consent.purpose.${text.purpose}`) !== text.title ? (
                    <p className="text-xs text-gray-500">{t(`consent.purpose.${text.purpose}`)}</p>
                  ) : null}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Badge tone="info">{t('consents.version', { version: text.version })}</Badge>
                    <Badge tone={text.isDefault ? 'gray' : 'brand'}>
                      {t(text.isDefault ? 'consents.default' : 'consents.own')}
                    </Badge>
                  </div>
                </div>
                {canEdit ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    aria-label={t('consents.editFor', { title: text.title })}
                    onClick={() => {
                      setDraft({ title: text.title, body: text.body });
                      setEditing(text);
                    }}
                  >
                    {t('consents.edit')}
                  </Button>
                ) : null}
              </div>
              <details className="mt-3">
                <summary className="control-touch inline-flex cursor-pointer items-center text-sm font-medium text-brand-700 underline underline-offset-2 hover:decoration-2">
                  {t('consents.read')}
                </summary>
                <div className="mt-2 whitespace-pre-line text-sm text-gray-700">
                  {withSchool(text.body)}
                </div>
              </details>
            </section>
          ))}
        </div>
      </Card>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={t('consents.edit')}
        size="lg"
        actions={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="consent-text-form" loading={publish.isPending}>
              {t('consents.publish')}
            </Button>
          </>
        }
      >
        {editing ? (
          <form id="consent-text-form" onSubmit={submit} className="space-y-4">
            <p className="rounded-control bg-info-100 px-3 py-2 text-sm text-info-700">
              {t('consents.newVersion', { version: editing.version + 1 })}
            </p>
            <Input
              label={t('consents.field.title')}
              required
              minLength={3}
              maxLength={200}
              value={draft.title}
              onChange={(event) => setDraft({ ...draft, title: event.target.value })}
            />
            <div>
              <label
                htmlFor="consent-text-body"
                className="block text-sm font-medium text-gray-900"
              >
                {t('consents.field.body')}
              </label>
              <textarea
                id="consent-text-body"
                required
                minLength={50}
                maxLength={20000}
                rows={12}
                className="mt-1 w-full rounded-control border border-gray-300 p-2 text-sm"
                aria-describedby="consent-text-body-help"
                value={draft.body}
                onChange={(event) => setDraft({ ...draft, body: event.target.value })}
              />
              <p id="consent-text-body-help" className="mt-1 text-xs text-gray-500">
                {t('consents.field.body.help')}
              </p>
            </div>
          </form>
        ) : null}
      </Modal>
    </>
  );
};
